import { readable, derived } from "svelte/store";
import type { Readable } from "svelte/store";

function mediaQueryStore(query: string): Readable<boolean> {
    return readable(window.matchMedia(query).matches, (set) => {
        const mq = window.matchMedia(query);
        const onChange = () => set(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    });
}

const coarsePointer = mediaQueryStore('(pointer: coarse)');

/*
The device's portrait (shorter) dimension is at most 1024px, evaluated independently of
the current orientation: check the width in portrait, the height in landscape. This makes
the classification stable when the device is rotated — a phone or tablet that qualifies in
portrait keeps qualifying in landscape.
*/
const smallPortraitWidth = mediaQueryStore(
    '(orientation: portrait) and (max-width: 1024px), (orientation: landscape) and (max-height: 1024px)'
);

/*
Hybrid mobile detection: a touch device (coarse pointer) whose portrait width is at most
1024px (all phones, tablets). Such clients are locked to catalog mode regardless of the
current orientation. A device with a larger screen or a mouse attached is not considered
mobile.
*/
export const isMobileDevice: Readable<boolean> = derived(
    [coarsePointer, smallPortraitWidth],
    ([coarse, small]) => coarse && small
);

/*
Pure width breakpoint, aligned with Tailwind's `sm` (640px). Drives the stacked
row layout and the virtual-list row height on narrow screens, independent of
whether the device is touch-operated.
*/
export const isNarrowViewport: Readable<boolean> = mediaQueryStore('(max-width: 639px)');

/*
True whenever the viewport is wider than it is tall. Used together with
`isMobileDevice` to enforce portrait orientation on mobile: when a mobile client
is rotated to landscape, an overlay prompts the user to rotate back.
*/
export const isLandscape: Readable<boolean> = mediaQueryStore('(orientation: landscape)');
